import { Disclosure } from '@headlessui/react';
import { faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/form/SubmitButton';
import { Dialog } from '~/components/ui/Dialog';
import { HoverTooltip } from '~/components/ui/HoverTooltip';
import { Option } from '~/types';
import { SearchInput } from '../FindAddContact/SearchInput';
import { Pill } from '../ui/nucleus/Pill';
import {
  AssetDrivenTemplateIndicator,
  isAssetDrivenTemplate,
  type TemplateBehavior,
} from './AssetDrivenTemplateIndicator';
import { filterTemplateOptions } from './templatePicker';

type FormValues = {
  fromTemplate: string;
};

export type TemplateOption = Option & {
  /** Template behavior: 'static' for fixed tasks, 'asset_rules' for asset-driven */
  behavior?: TemplateBehavior;
};

type Props = {
  templateOptions: TemplateOption[];
  onSelect: (name: string) => void;
};

export const UseTemplateButton = ({ templateOptions, onSelect }: Props) => {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [query, setQuery] = useState('');

  const { control, reset, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      fromTemplate: '',
    },
  });

  // Reset form when dialog opens/closes
  // Effect is necessary to reset form state when dialog visibility changes
  useEffect(() => reset(), [reset, showDialog]);

  const filtered = useMemo(
    () => filterTemplateOptions(templateOptions, query),
    [query, templateOptions]
  );

  const hasAssetDrivenTemplates = filtered.assetDrivenTemplates.length > 0;
  const hasStaticTemplates = filtered.staticTemplates.length > 0;

  const handleFormSubmit = handleSubmit(({ fromTemplate }) => {
    if (fromTemplate) {
      onSelect(fromTemplate);
      setShowDialog(false);
    }
  });

  const renderTemplateOption = useCallback(
    (option: TemplateOption) => {
      const isAssetDriven = isAssetDrivenTemplate(option.behavior);

      return (
        <Controller
          control={control}
          name='fromTemplate'
          key={option.value}
          render={({ field }) => (
            <label className='relative flex items-center gap-2 py-1.5 pl-6 text-left'>
              <input
                {...field}
                type='radio'
                name='fromTemplate'
                className='absolute left-0 top-2.5 h-4 w-4'
                value={option.value}
              />
              <span className='flex-1'>{option.label}</span>
              {isAssetDriven && (
                <AssetDrivenTemplateIndicator size='sm' showTooltip={true} />
              )}
            </label>
          )}
        />
      );
    },
    [control]
  );

  return (
    <>
      <Dialog
        title={t('useTemplate')}
        show={showDialog}
        onClose={() => setShowDialog(false)}
      >
        <form onSubmit={handleFormSubmit} className='p-3 text-left'>
          <div className='mb-1'>
            <SearchInput setQuery={setQuery} query={query} />
          </div>
          <div className='mb-1 max-h-[50vh] overflow-y-auto'>
            {/* Asset-driven templates section - shown first */}
            {hasAssetDrivenTemplates && (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <Disclosure.Button className='mb-1 mt-2 flex w-full items-center gap-2 border-t border-outline-variant pt-3 text-start'>
                      <FontAwesomeIcon
                        className='text-copy-light'
                        icon={faCaretRight}
                        rotation={open ? 90 : undefined}
                        size='xs'
                      />
                      <span className='text-xs font-medium uppercase tracking-wide text-secondary'>
                        {t(
                          'job:templatePicker.assetDrivenTemplates',
                          'Asset-driven'
                        )}
                      </span>
                      <HoverTooltip
                        content={t(
                          'job:templatePicker.assetDrivenTooltip',
                          'These templates create tasks based on selected assets'
                        )}
                      >
                        <span className='cursor-help text-xs text-tertiary'>
                          ⓘ
                        </span>
                      </HoverTooltip>
                    </Disclosure.Button>
                    <Disclosure.Panel>
                      {filtered.assetDrivenTemplates.map(renderTemplateOption)}
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )}

            {/* Static templates section */}
            {hasStaticTemplates && (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <Disclosure.Button
                      className={classNames(
                        'mb-1 flex w-full items-center gap-2 text-start',
                        hasAssetDrivenTemplates
                          ? 'mt-3 border-t border-outline-variant pt-3'
                          : 'mt-2'
                      )}
                    >
                      <FontAwesomeIcon
                        className='text-copy-light'
                        icon={faCaretRight}
                        rotation={open ? 90 : undefined}
                        size='xs'
                      />
                      <span className='text-xs font-medium uppercase tracking-wide text-secondary'>
                        {t('job:templatePicker.standardTemplates', 'Templates')}
                      </span>
                    </Disclosure.Button>
                    <Disclosure.Panel>
                      {filtered.staticTemplates.map(renderTemplateOption)}
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )}

            {/* Empty state */}
            {filtered.all.length === 0 && (
              <div className='py-4 text-center text-sm text-secondary'>
                {t('job:templatePicker.noTemplatesFound', 'No templates found')}
              </div>
            )}
          </div>

          <div className='flex justify-end gap-3'>
            <button
              type='button'
              className='px-4 py-2 text-sm font-medium uppercase text-brand-dark transition-all duration-500 hover:text-brand'
              onClick={() => setShowDialog(false)}
            >
              {t('cancel')}
            </button>

            <Button
              type='submit'
              intent='primary'
              size='small'
              disabled={!watch('fromTemplate')}
            >
              {t('use')}
            </Button>
          </div>
        </form>
      </Dialog>
      {/* <Button onClick={() => setShowDialog(true)} intent='text' size='small'>
        {t('useTemplate')}
      </Button> */}
      <Pill hasParent onClick={() => setShowDialog(true)} active={showDialog}>
        {t('selectTemplate')}
      </Pill>
    </>
  );
};
